import type { ReactNode } from 'react';
import React, { createContext, useContext, useState } from 'react';

// Define types for the context state
interface SidebarContextType {
  isSidebarMinimized: boolean;
  toggleSidebar: () => void;
  setIsSidebarMinimized: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default value
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// Define the provider component
interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  const [isSidebarMinimized, setIsSidebarMinimized] = useState<boolean>(false);

  // Function to toggle the sidebar state
  const toggleSidebar = () => {
    setIsSidebarMinimized((prev) => !prev);
  };

  return (
    <SidebarContext.Provider
      value={{ isSidebarMinimized, toggleSidebar, setIsSidebarMinimized }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

// Custom hook to use the SidebarContext
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }

  return context;
};
